import { XHRAuthorizedClient } from '@/remote/client';
import { ENDPOINTS } from '@/remote/endpoints';
import type { ApiResponse } from '@/types/site';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../query-key';

// 사이트 사진 타입 정의
export interface SitePhotoResponse {
  id: string;
  siteId: string;
  title: string;
  filePath: string;
  createdTimestamp: number;
}

// 사이트 사진 목록 조회
export const useSitePhotosQuery = (siteId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.sites.getPhotos, siteId],
    queryFn: async (): Promise<SitePhotoResponse[]> => {
      const response = await XHRAuthorizedClient.get<
        ApiResponse<SitePhotoResponse[]>
      >(ENDPOINTS.sites.photos(siteId));
      return response.data.data;
    },
    enabled: !!siteId,
  });
};

// 사이트 사진 업로드
export const useUploadSitePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS.sites.uploadPhoto],
    mutationFn: async ({
      siteId,
      file,
      title,
    }: {
      siteId: string;
      file: File;
      title: string;
    }): Promise<SitePhotoResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);

      const response = await XHRAuthorizedClient.post<
        ApiResponse<SitePhotoResponse>
      >(ENDPOINTS.sites.photos(siteId), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('사진 업로드 오류:', error);
    },
  });
};

// 사이트 사진 삭제
export const useDeleteSitePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS.sites.deletePhoto],
    mutationFn: async ({
      siteId,
      photoId,
    }: {
      siteId: string;
      photoId: string;
    }): Promise<void> => {
      await XHRAuthorizedClient.delete<ApiResponse<void>>(
        ENDPOINTS.sites.deletePhoto(siteId, photoId)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('사진 삭제 오류:', error);
    },
  });
};
